import "@/index.css";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef } from "react";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import axios from "axios";
import LandingPage from "@/pages/LandingPage";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function AuthCallback() {
  const { googleLogin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const sessionId = new URLSearchParams(location.hash.slice(1)).get("session_id");
    (async () => {
      try {
        const { data } = await axios.post(`${API}/auth/session`, { session_id: sessionId }, { withCredentials: true });
        googleLogin(data);
        navigate("/admin", { replace: true, state: { user: data } });
      } catch (err) {
        const detail = err.response?.data?.detail;
        navigate("/admin/login", { replace: true, state: { error: typeof detail === "string" ? detail : "Acceso con Google fallido" } });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-[#0d0914] flex items-center justify-center" data-testid="auth-callback">
      <span className="f-mono text-xs uppercase tracking-widest text-[#e879f9] animate-pulse">Conectando con Google...</span>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) {
    return (
      <div className="min-h-screen bg-[#0d0914] flex items-center justify-center" data-testid="auth-loading">
        <span className="f-mono text-xs uppercase tracking-widest text-[#e879f9] animate-pulse">Verificando acceso...</span>
      </div>
    );
  }
  if (!user || user.role !== "super_ceo_admin") return <Navigate to="/admin/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detecta session_id en el fragment de forma síncrona durante el render (evita condiciones de carrera)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<LandingPage lang="es" />} />
      <Route path="/en" element={<LandingPage lang="en" />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/admin/login" element={<AdminLogin />} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <Toaster theme="dark" position="bottom-right" />
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
